import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

interface SecretInputProps {
  secretKey: string;
  applicationId: string;
  onSecretSet?: (key: string) => void;
}

export const SecretInput: React.FC<SecretInputProps> = ({
  secretKey,
  applicationId,
  onSecretSet
}) => {
  const [value, setValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useAuth();

  const handleSubmit = async () => {
    if (!value.trim()) {
      setError('Value is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/applications/${applicationId}/vault`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          key: secretKey,
          value: value,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to store secret');
      }

      setIsSubmitted(true);
      onSecretSet?.(secretKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to store secret');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="my-4 inline-block max-w-2xl">
        <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xs font-semibold text-gray-600">Key:</span>
            <span className="font-mono text-sm">{secretKey}</span>
            <span className="text-xs font-semibold text-gray-600 ml-4">Value:</span>
            <span className="font-mono text-sm text-gray-400">••••••••••••</span>
          </div>
          <div className="flex items-center gap-2 text-green-600">
            <Check className="h-4 w-4" />
            <span className="text-xs font-medium">Saved</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-4 inline-block max-w-2xl">
      <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="text-xs font-semibold text-gray-600 mb-2">Key</div>
            <div className="font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200 whitespace-nowrap">
              {secretKey}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-gray-600 mb-2">Value</div>
            <div className="flex gap-2">
              <div className="flex-1 relative min-w-0">
                <Input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter secret value"
                  className="font-mono text-sm pr-10"
                  style={showValue ? {} : { WebkitTextSecurity: 'disc' } as React.CSSProperties}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  data-1p-ignore
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowValue(!showValue)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isSubmitting}
                >
                  {showValue ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !value.trim()}
                size="sm"
                className="px-6 flex-shrink-0"
              >
                {isSubmitting ? 'Saving...' : 'Set'}
              </Button>
            </div>
          </div>
        </div>
        {error && (
          <div className="mt-2 text-xs text-red-600">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
